import { useEffect, useState } from "react";

import { CalendarErrorContext, CalendarErrorModalState } from "./components/Meetings/modals";

import { isCalendarErrorRequiringModal } from "./utils";

import { MeetingFormSchema } from "./schema";

/**
 * Hook to automatically clear validation errors when form values change
 *
 * @param formValues Current form values
 * @param validationErrors Current validation errors
 * @param setValidationErrors Function to update validation errors
 */
export const useValidationErrorClearer = (
	formValues: MeetingFormSchema,
	validationErrors: Partial<Record<keyof MeetingFormSchema, string>>,
	setValidationErrors: (errors: Partial<Record<keyof MeetingFormSchema, string>>) => void
) => {
	useEffect(() => {
		if (Object.keys(validationErrors).length > 0) {
			const newErrors = { ...validationErrors };
			let errorsChanged = false;

			// Check each field and clear its error if it now has a valid value
			if (newErrors.name && formValues.name.trim()) {
				delete newErrors.name;
				errorsChanged = true;
			}

			if (newErrors.description && formValues.description?.trim() && formValues.description.length <= 2000) {
				delete newErrors.description;
				errorsChanged = true;
			}

			if (newErrors.startDate && formValues.startDate) {
				delete newErrors.startDate;
				errorsChanged = true;
			}

			if (newErrors.endDate && formValues.endDate) {
				delete newErrors.endDate;
				errorsChanged = true;
			}

			if (newErrors.startTime && formValues.startTime) {
				delete newErrors.startTime;
				errorsChanged = true;
			}

			if (newErrors.endTime && formValues.endTime) {
				delete newErrors.endTime;
				errorsChanged = true;
			}

			if (errorsChanged) {
				setValidationErrors(newErrors);
			}
		}
	}, [formValues, validationErrors, setValidationErrors]);
};

/**
 * Shared logic hook for workspace details pages
 * Contains common state management and utility functions
 */
export const useWorkspaceDetailsLogic = (meetings: unknown[] = []) => {
	// State for storing IDs of meetings that have accordions expanded
	const defaultExpandedMeetingIds = meetings?.length ? [(meetings[0] as { _id: string })._id] : [];
	const [expandedMeetingIds, setExpandedMeetingIds] = useState<string[]>(defaultExpandedMeetingIds);

	// Function for updating expanded accordions
	const handleToggleAccordion = (meetingId: string) => {
		if (expandedMeetingIds.includes(meetingId)) {
			setExpandedMeetingIds((prev) => {
				const filteredMeetingIds = prev.filter((expandedMeetingId) => expandedMeetingId !== meetingId);
				return [...filteredMeetingIds];
			});
		} else {
			setExpandedMeetingIds((prev) => [...prev, meetingId]);
		}
	};

	// Transform workspace participants for AvatarGroup
	const transformWorkspaceParticipants = (participants: Array<{ userId: string; name?: string }> = []) =>
		participants.map((participant) => ({
			_id: participant.userId,
			name: participant.name || "",
		}));

	// Count active filters for the filter button badge
	const calculateActiveFiltersCount = (filters: Record<string, unknown>) => {
		return Object.entries(filters).filter(([key, value]) => {
			if (Array.isArray(value)) {
				return value.length > 0;
			}
			// Handle date range as a single filter if either start or end is set
			if (key === "meetingDateStart" && (filters.meetingDateStart || filters.meetingDateEnd)) {
				return true;
			}
			if (key === "meetingDateEnd") {
				return false; // Don't double count the date range
			}
			return value !== undefined && value !== "any";
		}).length;
	};

	// Determine if any filters are applied (with support for admin vs regular filters)
	const getHasFiltersApplied = (filters: Record<string, unknown>, isAdmin = false): boolean => {
		const statusFilter = filters.status;
		const hasStatusFilter = Boolean(statusFilter && Array.isArray(statusFilter) && statusFilter.length > 0);

		// Check for date range filters (applicable to both admin and regular users)
		const hasDateRangeFilter = Boolean(filters.meetingDateStart || filters.meetingDateEnd);

		if (isAdmin) {
			// Admin has status and date range filters
			return hasStatusFilter || hasDateRangeFilter;
		}

		// Regular user has status, privateNotes, and date range filters
		const privateNotesFilter = filters.privateNotes;
		const hasPrivateNotesFilter = Boolean(privateNotesFilter && privateNotesFilter !== "any");

		return hasStatusFilter || hasPrivateNotesFilter || hasDateRangeFilter;
	};

	return {
		expandedMeetingIds,
		setExpandedMeetingIds,
		handleToggleAccordion,
		transformWorkspaceParticipants,
		calculateActiveFiltersCount,
		getHasFiltersApplied,
	};
};

/**
 * Custom hook to manage main navigation active state for meeting workspace details
 * Dynamically sets the correct navigation item based on admin status and participation
 */
export const useWorkspaceNavigationState = ({
	isAdmin,
	isParticipant,
}: {
	isAdmin: boolean;
	isParticipant: boolean;
}) => {
	useEffect(() => {
		const updateMainNavigation = () => {
			// Remove active class from all main nav items
			const allNavItems = document.querySelectorAll("#header .links .top-item");
			allNavItems.forEach((item) => item.classList.remove("active"));

			// Add active class to the appropriate nav item based on view
			if (isAdmin && !isParticipant) {
				// Admin view: activate Company Info nav (globe icon with title="Company Info")
				const companyNavItem = document.querySelector('#header .links a[title="Company Info"]');
				if (companyNavItem) {
					companyNavItem.classList.add("active");
				}
			} else {
				// Participant view: activate Meeting Workspaces nav
				const meetingWorkspacesNavItem = document.querySelector(
					'#header .links a[href="/meeting-workspaces/"]'
				);
				if (meetingWorkspacesNavItem) {
					meetingWorkspacesNavItem.classList.add("active");
				}
			}
		};

		// Update navigation after component mounts and when view determination changes
		updateMainNavigation();
	}, [isAdmin, isParticipant]);
};

/**
 * Simplified calendar error handler hook
 * Only shows modals for specific cases that require user follow-up actions.
 * All other errors will fall through to default error handling (generic "Meeting Update Failed" toast).
 */
export const useCalendarErrorHandler = () => {
	const [modalState, setModalState] = useState<CalendarErrorModalState>({ type: "none" });

	/**
	 * Handle calendar errors - only shows modal for specific cases requiring user decisions
	 * Returns true if error was handled (modal shown), false if default error handling should take over
	 */
	const handleCalendarError = (error: any, context: CalendarErrorContext) => {
		const { requiresModal, errorType } = isCalendarErrorRequiringModal(error);

		if (!requiresModal || !errorType) {
			// Not a calendar error requiring modal, let default error handling take over
			return false;
		}

		// Show modal for errors that require user decisions
		setModalState({ type: "error", errorType, context, isRetrying: false });
		return true;
	};

	/**
	 * Close the calendar error modal
	 */
	const closeModal = () => {
		setModalState({ type: "none" });
	};

	/**
	 * Set modal to retrying state
	 */
	const setRetrying = (retrying: boolean) => {
		if (modalState.type === "error") {
			setModalState({ ...modalState, isRetrying: retrying });
		}
	};

	return {
		modalState,
		handleCalendarError,
		closeModal,
		setRetrying,
	};
};
